import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { GiftIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  DataTable,
  Drawer,
  EmptyState,
  ErrorNotice,
  Field,
  Pagination,
  Panel,
  SearchInput,
  SectionHeader,
  SelectInput,
  TextInput,
  type DataTableHeader,
  type DataTableSort,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import {
  addEventDonationItem,
  removeEventDonationItem,
  updateEventDonationItem,
  type DonationItem,
  type DonationItemInput,
  type DonationKind,
} from '@/lib/events';

/**
 * The list of what was donated on a CSR event (Roy's portal list, 2026-09-27).
 *
 * BCE records each item with the charity: what it was and how many. An item
 * given a catalog kind adds its quantity to the client's impact totals on
 * Analytics, so the totals are always the sum of this list. The client reads
 * the same list on their Charity tab.
 */

/** §40's catalog, in the order the picker lists it. Mirrors the API's `DONATION_KINDS`. */
export const DONATION_KIND_OPTIONS: readonly { readonly key: DonationKind; readonly label: string }[] = [
  { key: 'bicycles', label: 'Bicycles donated' },
  { key: 'guitars', label: 'Guitars donated' },
  { key: 'stemKits', label: 'STEM and educational kits' },
  { key: 'carePackages', label: 'Care packages assembled' },
  { key: 'foodPounds', label: 'Pounds of food' },
  { key: 'toys', label: 'Toys donated' },
];

/** The API's limits on a line (`DONATION_ITEM_LIMITS`). */
export const DONATION_ITEM_LIMITS = {
  descriptionMaxLength: 200,
  maxQuantity: 1_000_000,
} as const;

/** The picker value for an item that counts toward no total. */
const NO_KIND = '';
const PAGE_SIZE = 10;

const SORT_FIELDS = { item: 'description', countsToward: 'kindLabel', quantity: 'quantity' } as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.item, direction: 'asc' };
const HEADERS: readonly DataTableHeader[] = [
  { label: 'Item', sortKey: SORT_FIELDS.item },
  { label: 'Counts toward', sortKey: SORT_FIELDS.countsToward },
  { label: 'Quantity', sortKey: SORT_FIELDS.quantity },
];

function kindLabel(kind: DonationKind | undefined): string {
  return DONATION_KIND_OPTIONS.find((option) => option.key === kind)?.label ?? '';
}

function isDonationKind(value: string): value is DonationKind {
  return DONATION_KIND_OPTIONS.some((option) => option.key === value);
}

/** The drafts behind the drawer's fields. */
interface ItemDraft {
  readonly description: string;
  readonly quantity: string;
  readonly kind: string;
}

/**
 * Checks a draft the way the API will, so the drawer can say what is wrong
 * before anything is sent.
 *
 * @param draft - The fields as typed.
 * @returns The line to send, or the problem.
 */
export function parseItemDraft(
  draft: ItemDraft,
): { readonly ok: true; readonly item: DonationItemInput } | { readonly ok: false; readonly error: string } {
  const description = draft.description.trim();
  if (description.length === 0) return { ok: false, error: 'Say what was donated.' };
  if (description.length > DONATION_ITEM_LIMITS.descriptionMaxLength) {
    return { ok: false, error: `Keep the item to ${DONATION_ITEM_LIMITS.descriptionMaxLength} characters.` };
  }
  const quantity = Number(draft.quantity.trim());
  if (draft.quantity.trim() === '' || !Number.isInteger(quantity) || quantity < 1) {
    return { ok: false, error: 'The quantity is a whole number, at least 1.' };
  }
  if (quantity > DONATION_ITEM_LIMITS.maxQuantity) {
    return { ok: false, error: `The quantity is at most ${DONATION_ITEM_LIMITS.maxQuantity.toLocaleString('en-US')}.` };
  }
  return { ok: true, item: { description, quantity, kind: isDonationKind(draft.kind) ? draft.kind : null } };
}

/** "25 Bicycles donated · 60 Care packages assembled", or null when nothing counts. */
function totalsLine(counts: Readonly<Record<string, number>> | null | undefined): string | null {
  const parts = DONATION_KIND_OPTIONS.flatMap((option) => {
    const total = counts?.[option.key] ?? 0;
    return total > 0 ? [`${total.toLocaleString('en-US')} ${option.label}`] : [];
  });
  return parts.length > 0 ? parts.join(' · ') : null;
}

const DRAWER_MODE = { add: 'add', edit: 'edit' } as const;
type DrawerState =
  | { readonly mode: typeof DRAWER_MODE.add }
  | { readonly mode: typeof DRAWER_MODE.edit; readonly item: DonationItem }
  | null;

export interface DonationItemsSectionProps {
  readonly eventId: string;
  /** The client, as the copy names them. */
  readonly clientName: string;
  readonly items: readonly DonationItem[];
  /** The totals the API derived from the list. */
  readonly counts: Readonly<Record<string, number>> | null | undefined;
}

export function DonationItemsSection({ eventId, clientName, items, counts }: DonationItemsSectionProps): React.ReactElement {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);

  const rows = items.map((item) => ({ ...item, kindLabel: kindLabel(item.kind) }));
  const needle = search.trim().toLowerCase();
  const matched = sortRows(
    needle === '' ? rows : rows.filter((row) => `${row.description} ${row.kindLabel}`.toLowerCase().includes(needle)),
    sort ?? DEFAULT_SORT,
  );
  const totalPages = Math.max(1, Math.ceil(matched.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const shown = matched.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const totals = totalsLine(counts);

  const addButton = (
    <Button guide="donations-add" size="compact" onClick={() => setDrawer({ mode: DRAWER_MODE.add })}>
      <PlusIcon aria-hidden className="size-4" />
      Add item
    </Button>
  );

  return (
    <section data-guide="donations-section">
      <SectionHeader
        title="What was donated"
        description={`Each item recorded with the charity. ${clientName} sees this list on their Charity tab.`}
        aside={items.length > 0 ? addButton : undefined}
      />
      {items.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<GiftIcon aria-hidden className="size-6" />}
            title="No donated items yet"
            message="List what the group gave, one line per item. Pick what each one counts toward and it adds to the client's impact totals."
            action={addButton}
          />
        </Panel>
      ) : (
        <>
          <SearchInput
            label="Search donated items"
            placeholder="Search items"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            className="mb-3 max-w-sm"
          />
          {matched.length === 0 ? (
            <Panel>
              <EmptyState
                icon={<GiftIcon aria-hidden className="size-6" />}
                title="No items match"
                message="Try a different word, or clear the search to see every item."
              />
            </Panel>
          ) : (
            <DataTable guide="donations-table" headers={HEADERS} rightAlignedHeaders={[2]} sort={sort ?? DEFAULT_SORT} onSort={onSort}>
              {shown.map((row) => (
                <tr key={row.id} className="border-t border-portal-line align-middle">
                  <td className="p-3 text-portal-small">
                    <button
                      type="button"
                      onClick={() => setDrawer({ mode: DRAWER_MODE.edit, item: row })}
                      className="text-left font-bold text-brand underline-offset-2 hover:underline"
                    >
                      {row.description}
                    </button>
                  </td>
                  <td className="p-3 text-portal-small text-portal-muted">{row.kindLabel || 'No total'}</td>
                  <td className="p-3 text-right text-portal-small whitespace-nowrap">{row.quantity.toLocaleString('en-US')}</td>
                </tr>
              ))}
            </DataTable>
          )}
          <Pagination
            page={current}
            totalPages={totalPages}
            onChange={setPage}
            summary={`${(current - 1) * PAGE_SIZE + 1}–${(current - 1) * PAGE_SIZE + shown.length} of ${matched.length}`}
          />
          <p data-guide="donations-totals" className="mt-3 text-portal-micro text-portal-muted">
            {totals === null
              ? 'None of these items counts toward an impact total yet. Pick what an item counts toward to add it.'
              : `Adds to the client's impact totals: ${totals}.`}
          </p>
        </>
      )}

      <DonationItemDrawer
        key={drawer === null ? 'closed' : drawer.mode === DRAWER_MODE.edit ? drawer.item.id : DRAWER_MODE.add}
        eventId={eventId}
        state={drawer}
        onClose={() => setDrawer(null)}
      />
    </section>
  );
}

/**
 * Adds a donated item, or edits or removes one.
 *
 * @param props - The event, which item (or a new one), and how to close.
 * @returns The drawer.
 */
function DonationItemDrawer({
  eventId,
  state,
  onClose,
}: {
  readonly eventId: string;
  readonly state: DrawerState;
  readonly onClose: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const editing = state?.mode === DRAWER_MODE.edit ? state.item : null;
  const [draft, setDraft] = useState<ItemDraft>({
    description: editing?.description ?? '',
    quantity: editing ? String(editing.quantity) : '',
    kind: editing?.kind ?? NO_KIND,
  });
  const [problem, setProblem] = useState<string | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['event-csr', eventId] });
  }

  const save = useMutation({
    mutationFn: (item: DonationItemInput) =>
      editing ? updateEventDonationItem(eventId, editing.id, item) : addEventDonationItem(eventId, item),
    onSuccess: async (_plan, item) => {
      await refresh();
      toast.success(editing ? `Saved ${item.description}` : `Added ${item.description}`);
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: (item: DonationItem) => removeEventDonationItem(eventId, item.id),
    onSuccess: async (_plan, item) => {
      await refresh();
      toast.success(`Removed ${item.description}`);
      setConfirmingRemove(false);
      onClose();
    },
  });

  const busy = save.isPending || remove.isPending;
  const set = (field: keyof ItemDraft) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setDraft((previous) => ({ ...previous, [field]: event.target.value }));

  function submit(): void {
    const parsed = parseItemDraft(draft);
    if (!parsed.ok) {
      setProblem(parsed.error);
      return;
    }
    setProblem(null);
    save.mutate(parsed.item);
  }

  return (
    <>
      <Drawer
        open={state !== null && !confirmingRemove}
        title={editing ? 'Donated item' : 'Add a donated item'}
        subtitle="Recorded with the charity. The client sees it on their Charity tab."
        onClose={() => {
          if (!busy) onClose();
        }}
        footer={
          <div className="flex items-center justify-between gap-2">
            {editing ? (
              <Button variant="ghost" onClick={() => setConfirmingRemove(true)} disabled={busy}>
                <Trash2Icon aria-hidden className="size-4" />
                Remove
              </Button>
            ) : (
              <span />
            )}
            <span className="flex gap-2">
              <Button variant="secondary" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button guide="donation-submit" onClick={submit} disabled={busy}>
                {save.isPending ? 'Saving…' : editing ? 'Save' : 'Add item'}
              </Button>
            </span>
          </div>
        }
      >
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <Field guide="donation-description" label="What was donated" htmlFor="donation-item-description" required hint="As the client should read it, e.g. Kids' bicycles with helmets">
            <TextInput
              id="donation-item-description"
              value={draft.description}
              maxLength={DONATION_ITEM_LIMITS.descriptionMaxLength}
              onChange={set('description')}
              autoFocus
            />
          </Field>
          <Field guide="donation-quantity" label="Quantity" htmlFor="donation-item-quantity" required>
            <TextInput
              id="donation-item-quantity"
              type="number"
              inputMode="numeric"
              min={1}
              max={DONATION_ITEM_LIMITS.maxQuantity}
              step={1}
              value={draft.quantity}
              onChange={set('quantity')}
              className="max-w-40"
            />
          </Field>
          <Field
            guide="donation-kind"
            label="Counts toward"
            htmlFor="donation-item-kind"
            hint="Adds the quantity to this total on the client's impact numbers. Leave it for an item no total covers."
          >
            <SelectInput id="donation-item-kind" value={draft.kind} onChange={set('kind')}>
              <option value={NO_KIND}>No total</option>
              {DONATION_KIND_OPTIONS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </SelectInput>
          </Field>
          {problem ? <ErrorNotice inline title="Check the item" message={problem} /> : null}
          {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}
        </form>
      </Drawer>

      <ConfirmDialog
        open={confirmingRemove && editing !== null}
        tone="danger"
        title={`Remove ${editing?.description ?? 'this item'}?`}
        message="It leaves the list the client sees, and its quantity leaves their impact totals."
        confirmLabel="Remove"
        pending={remove.isPending}
        error={remove.isError ? errorMessage(remove.error) : undefined}
        onConfirm={() => {
          if (editing) remove.mutate(editing);
        }}
        onCancel={() => setConfirmingRemove(false)}
      />
    </>
  );
}
